/**
 * EpisodePipeline — Planned → In Production → Editing → Review → Approved,
 * with the same editorial gate the standalone page enforced.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, ChevronLeft, ChevronRight, Trash2, Check } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';

const STAGES = [
  ['planned', 'Planned'],
  ['in_production', 'In production'],
  ['editing', 'Editing'],
  ['review', 'Review'],
  ['approved', 'Approved'],
];

const blank = {
  programme_id: '', title: '', summary: '', scripture: '', rundown: '',
  rights_owner: '', rights_expiry: '', stage: 'planned', target_date: '',
};

const ReadinessChips = ({ readiness }) => (
  <div className="mt-3 flex flex-wrap gap-1.5">
    {Object.entries(readiness?.checks || {}).map(([key, ok]) => (
      <span key={key}
            className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
              ok ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
        {key}
      </span>
    ))}
  </div>
);

export const EpisodePipeline = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);

  const { data } = useQuery({
    queryKey: ['fwtv-episodes'],
    queryFn: () => api.get('/fwtv/studio/episodes').then((r) => r.data),
  });
  const { data: programmes } = useQuery({
    queryKey: ['fwtv-programmes-admin'],
    queryFn: () => api.get('/fwtv/admin/programmes').then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['fwtv-episodes'] });
    qc.invalidateQueries({ queryKey: ['fwtv-studio-overview'] });
    qc.invalidateQueries({ queryKey: ['fwtv-approvals'] });
    qc.invalidateQueries({ queryKey: ['fwtv-media-readiness'] });
  };

  const create = useMutation({
    mutationFn: () => api.post('/fwtv/studio/episodes', form),
    onSuccess: () => { toast.success('Episode added to the pipeline'); setOpen(false); setForm(blank); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not create the episode')),
  });

  const move = useMutation({
    mutationFn: ({ id, stage }) => api.post(`/fwtv/studio/episodes/${id}/stage`, { stage }),
    onSuccess: (_r, v) => { toast.success(v.stage === 'approved' ? 'Approved for transmission' : 'Moved'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not move the episode')),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/fwtv/studio/episodes/${id}`),
    onSuccess: () => { toast.success('Episode removed'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not remove the episode')),
  });

  const list = (programmes?.programmes || programmes || []);
  const byStage = data?.by_stage || {};
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  return (
    <div className="space-y-5" data-testid="fwtv-pipeline">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-600">
          Drag-free pipeline — move an episode forward when its stage is genuinely done.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="fwtv-new-episode-btn"><Plus className="h-4 w-4 mr-1" /> New episode</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New episode</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Programme</Label>
                <Select value={form.programme_id} onValueChange={set('programme_id')}>
                  <SelectTrigger data-testid="episode-programme"><SelectValue placeholder="Choose a programme" /></SelectTrigger>
                  <SelectContent>
                    {list.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Episode title</Label>
                <Input value={form.title} onChange={set('title')} data-testid="episode-title" /></div>
              <div><Label>Episode summary</Label>
                <Textarea value={form.summary} onChange={set('summary')} data-testid="episode-summary" /></div>
              <div><Label>Primary scripture</Label>
                <Input value={form.scripture} onChange={set('scripture')} data-testid="episode-scripture" /></div>
              <div><Label>Production rundown</Label>
                <Textarea value={form.rundown} onChange={set('rundown')} data-testid="episode-rundown" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Rights owner</Label>
                  <Input value={form.rights_owner} onChange={set('rights_owner')} data-testid="episode-rights-owner" /></div>
                <div><Label>Rights expiry</Label>
                  <Input type="date" value={form.rights_expiry} onChange={set('rights_expiry')} data-testid="episode-rights-expiry" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Production stage</Label>
                  <Select value={form.stage} onValueChange={set('stage')}>
                    <SelectTrigger data-testid="episode-stage"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STAGES.filter(([v]) => v !== 'approved').map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Target date</Label>
                  <Input type="date" value={form.target_date} onChange={set('target_date')} data-testid="episode-target-date" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => create.mutate()} disabled={!form.title || create.isPending}
                      data-testid="episode-create-btn">Create episode</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {STAGES.map(([value, label], si) => (
          <div key={value} className="space-y-3" data-testid={`pipeline-col-${value}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
              <Badge variant="outline">{(byStage[value] || []).length}</Badge>
            </div>
            {(byStage[value] || []).length === 0 && (
              <p className="text-xs text-slate-400">No episodes</p>
            )}
            {(byStage[value] || []).map((ep) => (
              <Card key={ep.id} data-testid={`episode-${ep.id}`}>
                <CardContent className="p-4">
                  <p className="font-semibold text-sm leading-snug">{ep.title}</p>
                  <p className="mt-1 text-xs text-slate-500">{ep.programme_title}</p>
                  {ep.target_date && (
                    <p className="mt-1 text-xs text-slate-400">Target {ep.target_date}</p>
                  )}
                  <ReadinessChips readiness={ep.readiness} />
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {si > 0 && (
                      <Button size="sm" variant="outline" data-testid={`episode-back-${ep.id}`}
                              onClick={() => move.mutate({ id: ep.id, stage: STAGES[si - 1][0] })}>
                        <ChevronLeft className="h-3 w-3" /> Back
                      </Button>
                    )}
                    {si < STAGES.length - 1 && (
                      <Button size="sm" variant={si === STAGES.length - 2 ? 'default' : 'outline'}
                              data-testid={`episode-forward-${ep.id}`}
                              onClick={() => move.mutate({ id: ep.id, stage: STAGES[si + 1][0] })}>
                        {si === STAGES.length - 2
                          ? <><Check className="h-3 w-3 mr-1" /> Approve</>
                          : <>{STAGES[si + 1][1]} <ChevronRight className="h-3 w-3" /></>}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`episode-delete-${ep.id}`}
                            onClick={() => remove.mutate(ep.id)}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500">
        Complete media checks and valid rights before approval.
      </p>
    </div>
  );
};

export default EpisodePipeline;
